import { Button, Form, message, Modal, Radio } from 'antd'
import { inject, observer } from 'mobx-react'
import React, { useEffect, useRef, useState } from 'react'
import RichEditor from '../../../../components/RichEditor/RichEditor'
import utils from '../../../../utils'
import { PdfEsignUltil } from '../components/PDFMerge'
import ExportPdf from '../ExportPdf/ExportPdf'
import ExportPdfV2 from '../ExportPdf/ExportPdfV2'

const FeedbackVotingPopup = ({
  visible,
  onClose,
  authenticationStore,
  FeedbackCommentStore,
  fetchFeedbackDetailData,
  feedbackDetailData,
  feedbackCommentDetail,
  fileStore,
  fetchDetailComment,
  selectedIndex,
  userList,
  userNameHaventVoting,
}) => {
  const {
    feedbackDetailDataById,
    feedbackCommentDataDetailV2,
  } = FeedbackCommentStore
  const [form] = Form.useForm()
  const EDITOR_REF = useRef(null)
  const [selectedOption, setSelectedOption] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isVoting, setIsVoting] = useState(false)
  const [currentComment, setCurrentComment] = useState('')
  const [editorContent, setEditorContent] = useState(null)
  const { currentUser } = authenticationStore

  const latestFeedbackForCurrentUser = feedbackCommentDataDetailV2?.feedbackCommentDetails
    .filter(
      item => item.feedbackUsername === currentUser.username && item.voteType
    )
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )[0]

  useEffect(() => {
    if (latestFeedbackForCurrentUser) {
      setSelectedOption(latestFeedbackForCurrentUser.voteType)
      form.setFieldsValue({
        voteType: latestFeedbackForCurrentUser.voteType || null,
      })
    } else if (feedbackDetailDataById) {
      form.setFieldsValue({
        voteType: feedbackDetailDataById?.voteType || null,
      })
    }
  }, [
    latestFeedbackForCurrentUser,
    feedbackDetailDataById,
    feedbackDetailData,
    form,
  ])

  // Helper function to get all comments from current user
  const getAllUserComments = () => {
    const oldComments = feedbackCommentDataDetailV2.feedbackCommentDetails
      .filter(c => c.feedbackUsername === currentUser.username)
      .map(c => c.detailFeedbackComment)
      .filter(comment => comment && comment.trim() !== '') // Filter out empty comments

    const newComment = EDITOR_REF.current?.editor?.getData() || ''

    // Only add new comment if it's not empty
    const allComments =
      newComment.trim() !== '' ? [...oldComments, newComment] : oldComments

    return allComments.join('<br/>')
  }

  const handleVoteSubmit = async () => {
    setIsVoting(true)
    if (utils.isNullish(selectedOption)) {
      message.error('Vui lòng nhập lựa chọn!')
      setIsVoting(false)
      return
    }

    try {
      // Get current editor content before it gets cleared
      const newComment = EDITOR_REF.current?.editor?.getData() || ''

      // Get all comments including existing ones
      const mergedComments = getAllUserComments()
      setCurrentComment(mergedComments)

      // Generate PDF for Part 1
      const fileListPart1 = await utils.savePdfToFile(
        'divToPrintPart1',
        `${currentUser?.code}_${new Date().getTime()}_part1`,
        'TimesNewRoman'
      )
      const filePart1 = fileListPart1[0]
      const formDataPart1 = new FormData()
      formDataPart1.append('file', filePart1)
      const responseSignDocPart1 = await fileStore.uploadFile(formDataPart1)

      // Generate PDF for Part 2
      const fileListPart2 = await utils.savePdfToFile(
        'divToPrintPart2',
        `${currentUser?.code}_${new Date().getTime()}_part2`,
        'TimesNewRoman'
      )
      const filePart2 = fileListPart2[0]
      const formDataPart2 = new FormData()
      formDataPart2.append('file', filePart2)
      const responseSignDocPart2 = await fileStore.uploadFile(formDataPart2)

      // Merge the two PDFs
      const mergedResult = await PdfEsignUltil.mergeCurrentPdfWithGenerated(
        responseSignDocPart1.data.file_id,
        responseSignDocPart2.data.file_id
      )

      const dataSubmit = {
        feedbackUsername: currentUser?.username,
        detailFeedbackComment: newComment, // Current editor content (could be empty or new comment)
        voteType: selectedOption,
        fileId: mergedResult.file_id,
        fileName: mergedResult.file_name,
      }

      if (latestFeedbackForCurrentUser) {
        // If there's already a voting record, update it
        // For updateVotingStatus, we need to check if there's new content in editor
        const hasNewComment = newComment && newComment.trim() !== ''

        if (hasNewComment) {
          // If there's new comment, include it
          await FeedbackCommentStore.updateVotingStatus(
            latestFeedbackForCurrentUser?.id,
            dataSubmit
          )
        } else {
          // If no new comment, just update the voting status without changing comment
          await FeedbackCommentStore.updateVotingStatus(
            latestFeedbackForCurrentUser?.id,
            {
              ...dataSubmit,
              detailFeedbackComment:
                latestFeedbackForCurrentUser.detailFeedbackComment, // Keep existing comment
            }
          )
        }
      } else {
        await FeedbackCommentStore.updateComment(
          dataSubmit,
          feedbackCommentDataDetailV2?.id
        )
      }

      if (latestFeedbackForCurrentUser) {
        await FeedbackCommentStore.getFeedbackCommentById(
          feedbackCommentDataDetailV2.id
        )
      }

      message.success('Thêm biểu quyết thành công!')
      fetchFeedbackDetailData()
      fetchDetailComment(feedbackCommentDataDetailV2.id)

      // Clear editor after successful vote submit
      if (EDITOR_REF.current?.editor) {
        EDITOR_REF.current.editor.setData('')
      }

      onClose()
    } catch (err) {
      console.error('Error in voting:', err)
      message.error('Có lỗi xảy ra khi lưu biểu quyết!')
    } finally {
      setIsVoting(false)
    }
  }

  const handleOk = async () => {
    try {
      await form.validateFields()
      setIsSubmitting(true)

      const newComment = EDITOR_REF.current?.editor?.getData() || ''

      // Check if new comment is empty
      if (!newComment.trim()) {
        message.error('Vui lòng nhập nội dung ý kiến!')
        setIsSubmitting(false)
        return
      }

      const oldComments = feedbackCommentDataDetailV2.feedbackCommentDetails
        .filter(c => c.feedbackUsername === currentUser.username)
        .map(c => c.detailFeedbackComment)
        .filter(comment => comment && comment.trim() !== '')

      const mergedComments = [...oldComments, newComment].join('<br/>')
      setCurrentComment(mergedComments)

      // Generate PDF for Part 1
      const fileListPart1 = await utils.savePdfToFile(
        'divToPrintPart1',
        `${currentUser?.code}_${new Date().getTime()}_part1`,
        'TimesNewRoman'
      )
      const filePart1 = fileListPart1[0]
      const formDataPart1 = new FormData()
      formDataPart1.append('file', filePart1)
      const responseSignDocPart1 = await fileStore.uploadFile(formDataPart1)

      // Generate PDF for Part 2
      const fileListPart2 = await utils.savePdfToFile(
        'divToPrintPart2',
        `${currentUser?.code}_${new Date().getTime()}_part2`,
        'TimesNewRoman'
      )
      const filePart2 = fileListPart2[0]
      const formDataPart2 = new FormData()
      formDataPart2.append('file', filePart2)
      const responseSignDocPart2 = await fileStore.uploadFile(formDataPart2)

      // Merge the two PDFs
      const mergedResult = await PdfEsignUltil.mergeCurrentPdfWithGenerated(
        responseSignDocPart1.data.file_id,
        responseSignDocPart2.data.file_id
      )

      const dataSubmit = {
        feedbackUsername: currentUser?.username,
        detailFeedbackComment: newComment,
        voteType: selectedOption,
        fileId: mergedResult.file_id,
        fileName: mergedResult.file_name,
      }

      await FeedbackCommentStore.updateComment(
        dataSubmit,
        feedbackCommentDataDetailV2?.id
      )

      if (latestFeedbackForCurrentUser) {
        await FeedbackCommentStore.getFeedbackCommentById(
          feedbackCommentDataDetailV2.id
        )
      }

      // Clear editor and form after successful comment submit
      setEditorContent(null)
      if (EDITOR_REF.current?.editor) {
        EDITOR_REF.current.editor.setData('')
      }
      form.resetFields()

      message.success('Đã thêm ý kiến!')
      fetchFeedbackDetailData()
      fetchDetailComment(feedbackCommentDataDetailV2.id)
    } catch (err) {
      console.error('Error in submitting comment:', err)
      message.error('Có lỗi xảy ra khi lưu ý kiến!')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    onClose()
  }

  const capitalize = str => {
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  function getNameUppercaseByUsername(username) {
    const user = userList?.find(user => user.username === username)
    return user ? capitalize(user.name_uppercase) : 'Admin'
  }

  return (
    <>
      <Modal
        title="Ý kiến biểu quyết"
        visible={visible}
        width={800}
        onCancel={handleCancel}
        footer={null}>
        {/* Voting Options Section */}
        {userNameHaventVoting.includes(currentUser.username) && (
          <div
            style={{
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              padding: '16px',
              marginBottom: '20px',
              backgroundColor: '#fafafa',
            }}>
            <div style={{ marginBottom: '12px', fontWeight: '500' }}>
              Nhập lựa chọn:
            </div>
            <Radio.Group
              onChange={e => setSelectedOption(e.target.value)}
              value={selectedOption}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                marginBottom: '16px',
              }}>
              <div
                style={{
                  padding: '12px 16px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  backgroundColor:
                    selectedOption === 'AGREE' ? '#e6f7ff' : '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  ...(selectedOption === 'AGREE' && {
                    borderLeft: '4px solid #1890ff',
                  }),
                }}>
                <Radio value="AGREE">
                  <span style={{ fontWeight: '500' }}>Đồng ý</span>
                </Radio>
              </div>
              <div
                style={{
                  padding: '12px 16px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  backgroundColor:
                    selectedOption === 'DISAGREE' ? '#e6f7ff' : '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  ...(selectedOption === 'DISAGREE' && {
                    borderLeft: '4px solid #1890ff',
                  }),
                }}>
                <Radio value="DISAGREE">
                  <span style={{ fontWeight: '500' }}>Không đồng ý</span>
                </Radio>
              </div>
              <div
                style={{
                  padding: '12px 16px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  backgroundColor:
                    selectedOption === 'OTHER' ? '#e6f7ff' : '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  ...(selectedOption === 'OTHER' && {
                    borderLeft: '4px solid #1890ff',
                  }),
                }}>
                <Radio value="OTHER">
                  <span style={{ fontWeight: '500' }}>
                    Không có ý kiến/Ý kiến khác
                  </span>
                </Radio>
              </div>
            </Radio.Group>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="primary"
                onClick={handleVoteSubmit}
                loading={isVoting}
                style={{ whiteSpace: 'nowrap' }}>
                Lưu lựa chọn
              </Button>
            </div>
          </div>
        )}
        <div
          style={{
            maxHeight: '500px',
            overflow: 'auto',
            marginBottom: '20px',
            paddingRight: '10px',
          }}>
          <div
            style={{
              fontWeight: 'bold',
              marginBottom: '16px',
              fontSize: 14,
              color: '#262626',
            }}>
            Danh sách ý kiến:
          </div>
          {feedbackCommentDataDetailV2?.feedbackCommentDetails?.map(
            comment =>
              comment.detailFeedbackComment !== null &&
              comment.detailFeedbackComment !== '' && (
                <div
                  key={comment.id}
                  style={{
                    marginBottom: '12px',
                    padding: '12px',
                    borderLeft: '3px solid #1890ff',
                    backgroundColor: '#f6f8fb',
                    borderRadius: '2px',
                  }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '8px',
                    }}>
                    <span style={{ fontWeight: '600', color: '#262626' }}>
                      {getNameUppercaseByUsername(comment.feedbackUsername)}:
                    </span>
                    <span
                      style={{
                        color: '#8c8c8c',
                        fontSize: '12px',
                        flexShrink: 0,
                        marginLeft: '10px',
                      }}>
                      {new Date(comment.createdAt).toLocaleDateString('vi-VN', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })}{' '}
                      {new Date(comment.createdAt).toLocaleTimeString('vi-VN', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>

                  <div
                    style={{ margin: 0, color: '#262626', lineHeight: '1.5' }}
                    dangerouslySetInnerHTML={{
                      __html: comment.detailFeedbackComment,
                    }}></div>
                </div>
              )
          )}
        </div>

        {/* Form Section for Comments */}
        <div
          style={{
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            padding: '16px',
            backgroundColor: '#fafafa',
            marginTop: '20px',
          }}>
          <Form
            form={form}
            name="feedback-comment"
            layout="vertical"
            onFinish={handleOk}>
            <Form.Item
              label={
                <span style={{ fontWeight: '500', color: '#262626' }}>
                  ý kiến khác
                </span>
              }
              name="comment"
              rules={[
                { required: true, message: 'Vui lòng nhập ý kiến của bạn!' },
              ]}>
              <RichEditor
                EDITOR_REF={EDITOR_REF}
                editorContent={editorContent}
                height={100}
                onEditorChange={content =>
                  form.setFieldsValue({ comment: content })
                }
              />
            </Form.Item>

            <Form.Item
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end',
                margin: 0,
              }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                style={{ whiteSpace: 'nowrap', marginRight: '8px' }}>
                Lưu ý kiến
              </Button>
              <Button htmlType="button" onClick={handleCancel}>
                Đóng
              </Button>
            </Form.Item>
          </Form>
        </div>
      </Modal>
      <div style={{ display: 'none' }}>
        <ExportPdf
          feedbackDetailData={feedbackDetailData}
          selectedIndex={selectedIndex}
        />
        <ExportPdfV2
          feedbackDetailData={feedbackDetailData}
          selectedOption={selectedOption}
          comment={
            !utils.isNullish(currentComment) ? currentComment : currentComment
          }
          selectedIndex={selectedIndex}
        />
      </div>
    </>
  )
}

export default inject(
  'authenticationStore',
  'FeedbackCommentStore',
  'fileStore'
)(observer(FeedbackVotingPopup))
